import { useMemo } from 'react';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import useGraphqlQuery from '@elementary/common-ui/hooks/useGraphqlQuery';
import { isInSaaSMode } from '@elementary/common-ui/utils/common';
import {
  parseApiTestResultTableData,
  parseReportTestResult,
  type ApiTestResultsTableData,
} from '@elementary/common-ui/components';
import { Report } from '@elementary/common-ui/types';
import { getAssetTestResultsQuery } from './api';

const useReportTests = (
  testResultsData: Report['test_results'] | undefined,
  assetId?: string,
) => {
  const testResults = useMemo(
    () => (assetId ? testResultsData?.[assetId] || [] : []),
    [testResultsData, assetId],
  );

  const tableData = useMemo(
    () => testResults.map(parseReportTestResult),
    [testResults],
  );
  return {
    tableData,
    isFetching: false,
    isError: false,
  };
};

const useApiTests = (
  testResultsData: Report['test_results'] | undefined,
  assetId?: string,
) => {
  const envid = useSelectedEnv();
  const { data, isFetching, isError } = useGraphqlQuery<
    ApiTestResultsTableData[]
  >({
    queryKey: ['lineage-asset-test-results', envid, assetId],
    query: getAssetTestResultsQuery,
    select: (data) => data.latestTestExecutions,
    variables: { assetId },
    enabled: !!assetId,
  });

  const tableData = useMemo(
    () => data?.map(parseApiTestResultTableData) || [],
    [data],
  );

  return {
    tableData,
    isFetching,
    isError,
  };
};

export const useInfoPanelTestResults = (
  testResultsData: Report['test_results'] | undefined,
  assetId?: string,
) => {
  const reportResult = useReportTests(testResultsData, assetId);
  const apiResult = useApiTests(testResultsData, assetId);

  return isInSaaSMode() ? apiResult : reportResult;
};
